import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ICustomerPayment } from "@/data/model/CustomerPayment";
import { Currency } from "@/data/model/Enums";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";

import { TastierClient } from "@/app/TastierClient";
import { BaseController, ViewTransition } from "@/app/controller/BaseController";
import { trans } from "@/app/localization/Trans";
import { Popup } from "@/app/ui/Popup";

export class PaymentHistoryController extends BaseController {
    public onGoBack?: { () };
    public onClick?: { (model: ICustomerPayment) };

    private readonly $paymentList: JQuery;
    private currentOffset = 0;
    private entriesPerPage = 40;
    private isLoading = false;

    constructor() {
        super("paymenthistory");

        const hdr = $('<div class="head"><div class="title">' + trans.paymentHistory.title + "</div></div>").appendTo(
            this.$view
        );
        Ripple.bind($('<a class="action left" href="#"><i class="fal fa-arrow-left"></i></a>'))
            .prependTo(hdr)
            .click((e) => {
                e.preventDefault();
                if (this.onGoBack !== undefined) this.onGoBack();
            });

        this.$paymentList = $('<div class="payments scrollable" />').appendTo(this.$view);
    }

    protected onBecomeInvisible() {
        this.unbindLazyLoadScroll();
    }

    protected onBecomeVisible(param: unknown, transition: ViewTransition | undefined) {
        if (transition === undefined || transition === ViewTransition.Fade || transition === ViewTransition.SlideLeft) {
            TastierClient.context.loadFulfillmentsIfNeeded();

            this.isLoading = false;
            this.currentOffset = 0;
            this.$paymentList.empty().scrollTop(0);

            this.fetchPayments();
        }

        this.bindLazyLoadScroll();
    }

    private bindLazyLoadScroll() {
        this.$paymentList.unbind("scroll.paymenthistory").bind("scroll.paymenthistory", () => {
            const lastE = this.$paymentList.children().last();
            if (lastE.length > 0) {
                if (lastE.position().top < this.$paymentList.height() + 50) this.fetchPayments();
            }
        });
    }

    private unbindLazyLoadScroll() {
        this.$paymentList.unbind("scroll.paymenthistory");
    }

    private async fetchPayments() {
        if (this.isLoading) return;

        this.isLoading = true;
        const loader = $(
            '<div class="loading"><div class="loader"></div>' + trans.paymentHistory.loadingPayments + "</div>"
        ).appendTo(this.$paymentList);

        try {
            const c = await Factory.getInstance<CustomerDao>(CustomerDao).getPayments({
                offset: this.currentOffset,
                limit: this.entriesPerPage,
            });
            loader.remove();

            const totalItems = this.currentOffset + c.items.length;
            if (totalItems === 0) {
                this.$paymentList.append(
                    `<div class="nodata"><div class="lbl">${trans.paymentHistory.noPayments}</div></div>`
                );
                this.unbindLazyLoadScroll();
                return;
            }

            for (const item of c.items) {
                this.appendPayment(item);
            }

            if (!c.hasNext) {
                this.unbindLazyLoadScroll();
            }

            this.currentOffset += c.items.length;
            this.isLoading = false;
        } catch (ex) {
            logError(ex);
            Popup.show("Failed to load payments.");
            this.isLoading = false;
            this.unbindLazyLoadScroll();
        }
    }

    private appendPayment(payment: ICustomerPayment) {
        const o = $('<div class="payment" />').data("model", payment).appendTo(this.$paymentList);
        const io = $('<div class="inner" />').appendTo(o);

        if (payment.locationLogoPath !== undefined)
            io.append('<div class="thumb"><img src="' + payment.locationLogoPath + '" alt="Logo" /></div>');

        const d = $('<div class="details" />').appendTo(io);
        d.append("<h3>" + Format.htmlEscape(payment.locationName) + "</h3>");
        d.append(
            '<div class="date">' +
                DateTime.toDateString(payment.confirmDate ?? payment.created) +
                ", " +
                DateTime.toTimeString(payment.confirmDate ?? payment.created) +
                "</div>"
        );
        d.append('<div class="consumption">' + Format.toMoneyFormat(payment.amount, Currency.Chf) + "</div>");

        Ripple.bind(
            o.click((e) => {
                e.preventDefault();
                e.stopPropagation();

                if (this.onClick !== undefined) this.onClick(payment);
            })
        );
    }
}
